import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import api from '@/services/api';

interface Unit {
    id: number;
    name: string;
    code: string;
    contactName?: string;
    contactPhone?: string;
    usersCount: number;
    operationsCount: number;
}

interface Institution {
    id: number;
    name: string;
    code: string;
    type: 'POLICE' | 'INVESTIGATION' | 'PROSECUTION' | 'COLLABORATING' | 'OTHER';
    tags: string[];
    status: 'ACTIVE' | 'INACTIVE';
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    logoUrl?: string;
    units: Unit[];
    usersCount: number;
    operationsCount: number;
    createdAt: string;
}

const typeLabels = {
    POLICE: { labelKey: 'admin.agencies.types.POLICE', color: 'bg-blue-500/20 text-blue-400', icon: '🚔' },
    INVESTIGATION: { labelKey: 'admin.agencies.types.INVESTIGATION', color: 'bg-purple-500/20 text-purple-400', icon: '🔍' },
    PROSECUTION: { labelKey: 'admin.agencies.types.PROSECUTION', color: 'bg-orange-500/20 text-orange-400', icon: '⚖️' },
    COLLABORATING: { labelKey: 'admin.agencies.types.COLLABORATING', color: 'bg-emerald-500/20 text-emerald-400', icon: '🤝' },
    OTHER: { labelKey: 'admin.agencies.types.OTHER', color: 'bg-gray-500/20 text-gray-400', icon: '📋' },
};

// Datos de demostración con estructura jerárquica y roles normativos
const demoInstitutions: Institution[] = [
    {
        id: 1,
        name: 'Carabineros de Chile',
        code: 'CAR',
        type: 'POLICE',
        tags: ['Fuerza de Orden y Seguridad', 'Preventivo', 'Investigación Especializada'],
        status: 'ACTIVE',
        contactName: 'General Director',
        contactEmail: 'contacto@demo.vantak.io',
        contactPhone: '+56 2 9876 5432',
        address: 'Av. Libertador B. O\'Higgins 1196, Santiago',
        units: [
            { id: 101, name: 'Prefectura Santiago Centro', code: 'CAR-STC', contactName: 'Coronel Pérez', contactPhone: '+56 2 1111 1111', usersCount: 45, operationsCount: 89 },
            { id: 102, name: 'Grupo de Operaciones Policiales Especiales (GOPE)', code: 'GOPE', contactName: 'Mayor Muñoz', contactPhone: '+56 2 2222 2222', usersCount: 23, operationsCount: 45 },
            { id: 103, name: 'Departamento OS-9 (Inteligencia)', code: 'OS9', contactName: 'Capitán Silva', contactPhone: '+56 2 3333 3333', usersCount: 34, operationsCount: 67 },
            { id: 104, name: 'Prefectura Aeropolicial', code: 'CAR-AERO', contactName: 'Mayor Torres', contactPhone: '+56 2 4444 4444', usersCount: 12, operationsCount: 18 },
            { id: 105, name: 'LABOCAR (Laboratorio Criminalístico)', code: 'LABOCAR', contactName: 'Teniente López', contactPhone: '+56 2 5555 5555', usersCount: 8, operationsCount: 5 },
        ],
        usersCount: 122,
        operationsCount: 224,
        createdAt: '2024-01-15'
    },
    {
        id: 2,
        name: 'Policía de Investigaciones de Chile',
        code: 'PDI',
        type: 'INVESTIGATION',
        tags: ['Fuerza de Orden y Seguridad', 'Investigativo', 'Criminalística', 'Control Migratorio'],
        status: 'ACTIVE',
        contactName: 'Director General',
        contactEmail: 'contacto@demo.vantak.io',
        contactPhone: '+56 2 1234 5678',
        address: 'Av. Libertador B. O\'Higgins 1052, Santiago',
        units: [
            { id: 201, name: 'Brigada de Homicidios', code: 'PDI-BH', contactName: 'Subprefecto García', contactPhone: '+56 2 6666 6666', usersCount: 28, operationsCount: 56 },
            { id: 202, name: 'Brigada Antinarcóticos (BRIANCO)', code: 'BRIANCO', contactName: 'Subprefecto Soto', contactPhone: '+56 2 7777 7777', usersCount: 35, operationsCount: 78 },
            { id: 203, name: 'Brigada de Robos', code: 'PDI-BR', contactName: 'Comisario Díaz', contactPhone: '+56 2 8888 8888', usersCount: 22, operationsCount: 45 },
            { id: 204, name: 'Brigada de Delitos Sexuales (BRISEXME)', code: 'BRISEXME', contactName: 'Comisaria Fernández', contactPhone: '+56 2 9999 9999', usersCount: 18, operationsCount: 32 },
        ],
        usersCount: 103,
        operationsCount: 211,
        createdAt: '2024-01-15'
    },
    {
        id: 3,
        name: 'Fiscalía Nacional',
        code: 'FIS',
        type: 'PROSECUTION',
        tags: ['Persecución Penal', 'Dirección de Investigación'],
        status: 'ACTIVE',
        contactName: 'Fiscal Nacional',
        contactEmail: 'contacto@fiscalia.cl',
        contactPhone: '+56 2 6666 7777',
        address: 'Edificio Fiscalía Nacional, Santiago',
        units: [
            { id: 301, name: 'Fiscalía Metropolitana Centro', code: 'FIS-MC', contactName: 'Fiscal Regional', contactPhone: '+56 2 1010 1010', usersCount: 15, operationsCount: 28 },
            { id: 302, name: 'Unidad de Alta Complejidad', code: 'FIS-UAC', contactName: 'Fiscal Adjunto', contactPhone: '+56 2 2020 2020', usersCount: 8, operationsCount: 12 },
        ],
        usersCount: 23,
        operationsCount: 40,
        createdAt: '2024-03-15'
    },
    {
        id: 4,
        name: 'Dirección de Seguridad Municipal',
        code: 'DSM',
        type: 'COLLABORATING',
        tags: ['Seguridad Pública', 'Preventivo', 'Colaborador'],
        status: 'ACTIVE',
        contactName: 'Director de Seguridad',
        contactEmail: 'seguridad@municipio.cl',
        contactPhone: '+56 2 8888 9999',
        address: 'Municipalidad de Santiago',
        units: [
            { id: 401, name: 'Inspectores Territoriales', code: 'DSM-INSP', contactName: 'Jefe Operativo', contactPhone: '+56 9 8888 8888', usersCount: 45, operationsCount: 120 },
            { id: 402, name: 'Central de Cámaras', code: 'DSM-CAM', contactName: 'Supervisor Turno', contactPhone: '+56 2 8888 1400', usersCount: 12, operationsCount: 0 }
        ],
        usersCount: 57,
        operationsCount: 120,
        createdAt: '2024-02-01'
    },
    {
        id: 5,
        name: 'Servicio Nacional de Aduanas',
        code: 'SNA',
        type: 'COLLABORATING',
        tags: ['Fiscalización', 'Fronteras', 'Colaborador'],
        status: 'ACTIVE',
        contactName: 'Director Nacional',
        contactEmail: 'contacto@aduana.cl',
        contactPhone: '+56 32 2134 5678',
        address: 'Plaza Sotomayor 60, Valparaíso',
        units: [
            { id: 501, name: 'Aduana Metropolitana', code: 'SNA-MET', contactName: 'Director Regional', contactPhone: '+56 2 2999 9999', usersCount: 30, operationsCount: 312 },
            { id: 502, name: 'Unidad de Drogas y Delitos Conexos', code: 'SNA-UDDC', contactName: 'Jefe Unidad', contactPhone: '+56 32 2999 9991', usersCount: 15, operationsCount: 89 }
        ],
        usersCount: 85,
        operationsCount: 450,
        createdAt: '2024-01-20'
    },
    {
        id: 6,
        name: 'Gendarmería de Chile',
        code: 'GEN',
        type: 'COLLABORATING',
        tags: ['Seguridad Penitenciaria', 'Inteligencia', 'Colaborador'],
        status: 'ACTIVE',
        contactName: 'Director Nacional',
        contactEmail: 'director@gendarmeria.cl',
        contactPhone: '+56 2 2916 3000',
        address: 'Rosas 1264, Santiago',
        units: [
            { id: 601, name: 'Departamento de Inteligencia Penitenciaria', code: 'GEN-DIP', contactName: 'Jefe DIP', contactPhone: '+56 2 2916 3100', usersCount: 30, operationsCount: 150 },
            { id: 602, name: 'G.E.A.M. (Escoltas)', code: 'GEN-GEAM', contactName: 'Comandante', contactPhone: '+56 2 2916 3200', usersCount: 25, operationsCount: 80 }
        ],
        usersCount: 210,
        operationsCount: 340,
        createdAt: '2024-01-20'
    },
    {
        id: 7,
        name: 'Directemar (Armada de Chile)',
        code: 'DIR',
        type: 'COLLABORATING',
        tags: ['Policía Marítima', 'Fiscalización', 'Colaborador'],
        status: 'ACTIVE',
        contactName: 'Director General',
        contactEmail: 'directemar@directemar.cl',
        contactPhone: '+56 32 220 8000',
        address: 'Subida Cementerio 300, Valparaíso',
        units: [
            { id: 701, name: 'Policía Marítima', code: 'DIR-POLMAR', contactName: 'Gobernador Marítimo', contactPhone: '+56 32 220 8100', usersCount: 60, operationsCount: 200 }
        ],
        usersCount: 150,
        operationsCount: 200,
        createdAt: '2024-02-10'
    },
    {
        id: 8,
        name: 'Administración Central VANTAK',
        code: 'ADMIN',
        type: 'OTHER',
        tags: ['Administración', 'Soporte'],
        status: 'ACTIVE',
        contactName: 'Administrador del Sistema',
        contactEmail: 'admin@vantak.io',
        contactPhone: '+56 2 2345 6789',
        address: 'Edificio Gobierno, Santiago',
        units: [],
        usersCount: 5,
        operationsCount: 0,
        createdAt: '2024-01-01'
    },
];

export default function AgenciesPage() {
    const { t } = useTranslation();
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [loading, setLoading] = useState(true);
    const [showInstitutionModal, setShowInstitutionModal] = useState(false);
    const [showUnitModal, setShowUnitModal] = useState(false);
    const [editingInstitution, setEditingInstitution] = useState<Institution | null>(null);
    const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
    const [expandedInstitutions, setExpandedInstitutions] = useState<Set<number>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const [institutionForm, setInstitutionForm] = useState({
        name: '',
        code: '',
        type: 'POLICE' as Institution['type'],
        tags: [] as string[],
        status: 'ACTIVE' as Institution['status'],
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
    });

    const [unitForm, setUnitForm] = useState({
        name: '',
        code: '',
        contactName: '',
        contactPhone: '',
    });

    useEffect(() => {
        loadInstitutions();
    }, []);

    const loadInstitutions = async () => {
        try {
            const response = await api.get('/api/v1/institutions');
            setInstitutions(response.data);
        } catch {
            setInstitutions(demoInstitutions);
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (id: number) => {
        const newExpanded = new Set(expandedInstitutions);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedInstitutions(newExpanded);
    };

    const handleInstitutionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingInstitution) {
                await api.put(`/api/v1/institutions/${editingInstitution.id}`, institutionForm);
            } else {
                await api.post('/api/v1/institutions', institutionForm);
            }
            loadInstitutions();
        } catch {
            const newInstitution: Institution = {
                id: institutions.length + 1,
                ...institutionForm,
                units: [],
                usersCount: 0,
                operationsCount: 0,
                createdAt: new Date().toISOString(),
            };
            setInstitutions([...institutions, newInstitution]);
        }
        setShowInstitutionModal(false);
        resetInstitutionForm();
    };

    const handleUnitSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedInstitution) return;

        try {
            await api.post(`/api/v1/institutions/${selectedInstitution.id}/units`, unitForm);
            loadInstitutions();
        } catch {
            const newUnit: Unit = {
                id: Date.now(),
                ...unitForm,
                usersCount: 0,
                operationsCount: 0,
            };
            setInstitutions(institutions.map(inst =>
                inst.id === selectedInstitution.id
                    ? { ...inst, units: [...inst.units, newUnit] }
                    : inst
            ));
        }
        setShowUnitModal(false);
        setUnitForm({ name: '', code: '', contactName: '', contactPhone: '' });
    };

    const resetInstitutionForm = () => {
        setInstitutionForm({
            name: '', code: '', type: 'POLICE', tags: [], status: 'ACTIVE',
            contactName: '', contactEmail: '', contactPhone: '', address: '',
        });
        setEditingInstitution(null);
    };

    const openEditInstitution = (inst: Institution) => {
        setEditingInstitution(inst);
        setInstitutionForm({
            name: inst.name, code: inst.code, type: inst.type, tags: inst.tags || [], status: inst.status,
            contactName: inst.contactName || '', contactEmail: inst.contactEmail || '',
            contactPhone: inst.contactPhone || '', address: inst.address || '',
        });
        setShowInstitutionModal(true);
    };

    const openAddUnit = (inst: Institution) => {
        setSelectedInstitution(inst);
        setUnitForm({ name: '', code: '', contactName: '', contactPhone: '' });
        setShowUnitModal(true);
    };

    const filteredInstitutions = institutions.filter(inst =>
        inst.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inst.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inst.units.some(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalUsers = institutions.reduce((sum, i) => sum + i.usersCount, 0);
    const totalUnits = institutions.reduce((sum, i) => sum + i.units.length, 0);
    const totalOperations = institutions.reduce((sum, i) => sum + i.operationsCount, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('admin.agencies.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('admin.agencies.subtitle')}</p>
                </div>
                <button
                    data-testid="new-institution-button"
                    aria-label={t('admin.agencies.newInstitution')}
                    onClick={() => {
                        console.log('[Agencies] Nueva Institución clicked, opening modal');
                        resetInstitutionForm();
                        setShowInstitutionModal(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <span>🏛️</span>
                    {t('admin.agencies.newInstitution')}
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                    <div className="text-2xl font-bold text-white">{institutions.length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.agencies.stats.institutions')}</div>
                </div>
                <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
                    <div className="text-2xl font-bold text-purple-400">{totalUnits}</div>
                    <div className="text-gray-400 text-sm">{t('admin.agencies.stats.units')}</div>
                </div>
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                    <div className="text-2xl font-bold text-blue-400">{totalUsers}</div>
                    <div className="text-gray-400 text-sm">{t('admin.agencies.stats.totalUsers')}</div>
                </div>
                <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">{totalOperations}</div>
                    <div className="text-gray-400 text-sm">{t('admin.agencies.stats.operations')}</div>
                </div>
            </div>

            {/* Search */}
            <div>
                <input
                    type="text"
                    placeholder={t('admin.agencies.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500"
                />
            </div>

            {/* Institutions List */}
            <div className="space-y-4">
                {filteredInstitutions.map(institution => (
                    <div key={institution.id} className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
                        {/* Institution Header */}
                        <div
                            className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-700/30 transition-colors"
                            onClick={() => toggleExpand(institution.id)}
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl">
                                    {typeLabels[institution.type].icon}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-lg font-semibold text-white">{institution.name}</h3>
                                        <div className="flex gap-2">
                                            {institution.tags && institution.tags.length > 0 ? (
                                                institution.tags.map((tag, idx) => (
                                                    <span key={idx} className={`px-2 py-0.5 rounded text-xs border ${tag === 'Fuerza de Orden y Seguridad'
                                                        ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30'
                                                        : 'bg-gray-700/50 text-gray-300 border-gray-600'
                                                        }`}>
                                                        {tag}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className={`px-2 py-0.5 rounded text-xs ${typeLabels[institution.type].color}`}>
                                                    {t(typeLabels[institution.type].labelKey)}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-gray-500 text-sm">({institution.code})</span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                                        <span>👥 {t('admin.agencies.usersCount', { count: institution.usersCount })}</span>
                                        <span>📋 {t('admin.agencies.operationsCount', { count: institution.operationsCount })}</span>
                                        <span>🏢 {t('admin.agencies.unitsCount', { count: institution.units.length })}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={(e) => { e.stopPropagation(); openAddUnit(institution); }}
                                    className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-3 py-1 rounded-lg text-sm"
                                >
                                    + {t('admin.agencies.unit')}
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); openEditInstitution(institution); }}
                                    className="text-gray-400 hover:text-white p-2"
                                >
                                    ✏️
                                </button>
                                <span className={`transition-transform ${expandedInstitutions.has(institution.id) ? 'rotate-180' : ''}`}>
                                    ▼
                                </span>
                            </div>
                        </div>

                        {/* Units List (Expandable) */}
                        {expandedInstitutions.has(institution.id) && institution.units.length > 0 && (
                            <div className="border-t border-gray-700/50 bg-gray-900/30">
                                <div className="p-4 grid gap-2">
                                    {institution.units.map(unit => (
                                        <div key={unit.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                                                    🏢
                                                </div>
                                                <div>
                                                    <div className="text-white font-medium">{unit.name}</div>
                                                    <div className="text-gray-400 text-sm">{t('admin.agencies.unitMeta', { code: unit.code, contact: unit.contactName || t('admin.common.na') })}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-gray-400">
                                                <span>👥 {unit.usersCount}</span>
                                                <span>📋 {unit.operationsCount}</span>
                                                <button className="text-gray-400 hover:text-white p-1">✏️</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {expandedInstitutions.has(institution.id) && institution.units.length === 0 && (
                            <div className="border-t border-gray-700/50 p-6 text-center text-gray-400">
                                <p>{t('admin.agencies.noUnits')}</p>
                                <button
                                    onClick={() => openAddUnit(institution)}
                                    className="mt-2 text-purple-400 hover:text-purple-300"
                                >
                                    + {t('admin.agencies.addFirstUnit')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Institution Modal */}
            {showInstitutionModal && (
                <div role="dialog" aria-modal="true" data-testid="institution-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-xl border border-gray-700">
                        <h2 className="text-xl font-bold text-white mb-4">
                            {editingInstitution ? t('admin.agencies.editInstitution') : t('admin.agencies.newInstitution')}
                        </h2>
                        <form onSubmit={handleInstitutionSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.name')}</label>
                                <input
                                    type="text"
                                    value={institutionForm.name}
                                    onChange={(e) => setInstitutionForm({ ...institutionForm, name: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    placeholder={t('admin.agencies.form.namePlaceholder')}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.code')}</label>
                                    <input
                                        type="text"
                                        value={institutionForm.code}
                                        onChange={(e) => setInstitutionForm({ ...institutionForm, code: e.target.value.toUpperCase() })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white uppercase"
                                        placeholder={t('admin.agencies.form.codePlaceholderInstitution')}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.type')}</label>
                                    <select
                                        value={institutionForm.type}
                                        onChange={(e) => setInstitutionForm({ ...institutionForm, type: e.target.value as Institution['type'] })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value="POLICE">{t('admin.agencies.types.POLICE')}</option>
                                        <option value="INVESTIGATION">{t('admin.agencies.types.INVESTIGATION')}</option>
                                        <option value="PROSECUTION">{t('admin.agencies.types.PROSECUTION')}</option>
                                        <option value="COLLABORATING">{t('admin.agencies.types.COLLABORATING')}</option>
                                        <option value="OTHER">{t('admin.agencies.types.OTHER')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.contact')}</label>
                                    <input
                                        type="text"
                                        value={institutionForm.contactName}
                                        onChange={(e) => setInstitutionForm({ ...institutionForm, contactName: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.phone')}</label>
                                    <input
                                        type="tel"
                                        value={institutionForm.contactPhone}
                                        onChange={(e) => setInstitutionForm({ ...institutionForm, contactPhone: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.email')}</label>
                                <input
                                    type="email"
                                    value={institutionForm.contactEmail}
                                    onChange={(e) => setInstitutionForm({ ...institutionForm, contactEmail: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.address')}</label>
                                <input
                                    type="text"
                                    value={institutionForm.address}
                                    onChange={(e) => setInstitutionForm({ ...institutionForm, address: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => { setShowInstitutionModal(false); resetInstitutionForm(); }}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    {t('admin.common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                                >
                                    {editingInstitution ? t('admin.common.save') : t('admin.agencies.createInstitution')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Unit Modal */}
            {showUnitModal && selectedInstitution && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
                        <h2 className="text-xl font-bold text-white mb-2">{t('admin.agencies.newUnit')}</h2>
                        <p className="text-gray-400 text-sm mb-4">
                            <Trans
                                i18nKey="admin.agencies.addUnitTo"
                                values={{ name: selectedInstitution.name }}
                                components={{ name: <span className="text-white" /> }}
                            />
                        </p>
                        <form onSubmit={handleUnitSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.unitName')}</label>
                                <input
                                    type="text"
                                    value={unitForm.name}
                                    onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    placeholder={t('admin.agencies.form.unitNamePlaceholder')}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.code')}</label>
                                <input
                                    type="text"
                                    value={unitForm.code}
                                    onChange={(e) => setUnitForm({ ...unitForm, code: e.target.value.toUpperCase() })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white uppercase"
                                    placeholder={t('admin.agencies.form.codePlaceholderUnit')}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.contact')}</label>
                                    <input
                                        type="text"
                                        value={unitForm.contactName}
                                        onChange={(e) => setUnitForm({ ...unitForm, contactName: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.agencies.form.phone')}</label>
                                    <input
                                        type="tel"
                                        value={unitForm.contactPhone}
                                        onChange={(e) => setUnitForm({ ...unitForm, contactPhone: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowUnitModal(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    {t('admin.common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                                >
                                    {t('admin.agencies.createUnit')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
